import React, { FC, useEffect } from 'react';

import { ApolloError } from '@apollo/client';
import { useRouter } from 'expo-router';
import { useIntl } from 'react-intl';

import { CardModal } from 'components/card-modal';
import {
  IProcessSmgCodeMutation,
  LoyaltySmgCodeStatus,
  useProcessSmgCodeMutation,
} from 'generated/graphql-gateway';
import { useRoute } from 'hooks/navigation/use-route';
import { routes } from 'navigation/routing';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { useLoyaltyClaimPointsContext } from 'state/loyalty-claim-points';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';

import { LoadingAnimation } from './loyalty-claim-points.styled';
import { ClaimMethod } from './types';
import { getBackUrl } from './utils';

interface ClaimPointsRouteParams {
  code: string;
  transaction_date: string;
  method: ClaimMethod;
}

export enum LoyaltyCodeSubmittedStatus {
  COMPLETED = 'completed',
  FAILED = 'failed',
}

const LoyaltyClaimPointsProcess: FC<React.PropsWithChildren<unknown>> = () => {
  const { formatMessage } = useIntl();
  const { replace } = useRouter();
  const { loyaltyUser } = useLoyaltyUser();
  const { openModalWithStatus: onStatusChange } = useLoyaltyClaimPointsContext();
  const {
    params: { code, transaction_date, method },
  } = useRoute<ClaimPointsRouteParams>();

  const smgCode = code;
  const transactionDate = transaction_date;
  const baseUrl = routes.claimPointsBase;
  const backUrl = getBackUrl(formatMessage, baseUrl, method);

  const onMutationCompleted = (data: IProcessSmgCodeMutation) => {
    const result = data?.loyaltyProcessSmgCode;
    logEvent(CustomEventNames.LOYALTY_CODE_SUBMITTED, EventTypes.UserContent, {
      Code: smgCode,
      Method: ClaimMethod[method],
      Status: result.status,
      Result: LoyaltyCodeSubmittedStatus.COMPLETED,
    });

    // Only status update not needed is `accepted`
    if (result.status !== LoyaltySmgCodeStatus.ACCEPTED) {
      onStatusChange(result.status);
      return replace(backUrl);
    }

    if (result?.pointsEarned) {
      return replace({
        pathname: `${baseUrl}${routes.accepted}`,
        params: { earnedPoints: result.pointsEarned },
      });
    }
  };

  const onMutationError = (error: ApolloError) => {
    if (error?.graphQLErrors?.length) {
      const [err] = error.graphQLErrors;
      // @ts-expect-error TS(2571) FIXME: Object is of type 'unknown'.
      const status = err.extensions?.details?.status;
      onStatusChange(status);
      replace(backUrl);
      logEvent(CustomEventNames.LOYALTY_CODE_SUBMITTED, EventTypes.UserContent, {
        Code: smgCode,
        Method: ClaimMethod[method],
        Status: status,
        Result: LoyaltyCodeSubmittedStatus.FAILED,
      });
    } else {
      logEvent(CustomEventNames.LOYALTY_CODE_SUBMITTED, EventTypes.UserContent, {
        Code: smgCode,
        Method: ClaimMethod[method],
        Status: 'UNKNOWN',
        Result: LoyaltyCodeSubmittedStatus.FAILED,
      });
    }
  };

  const [processCode, { loading: processingSmgCode }] = useProcessSmgCodeMutation({
    onError: onMutationError,
    onCompleted: onMutationCompleted,
  });

  const loyaltyUserId = loyaltyUser?.id;

  useEffect(() => {
    if (smgCode && loyaltyUserId) {
      processCode({
        variables: {
          loyaltyId: loyaltyUserId,
          smgCode,
          ...(transactionDate && { transactionDate }),
        },
      });
    }
  }, [smgCode, loyaltyUserId, processCode, transactionDate]);

  if (processingSmgCode) {
    return (
      <CardModal>
        <LoadingAnimation />
      </CardModal>
    );
  }

  return null;
};

export default LoyaltyClaimPointsProcess;
